import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SortableLeadCard from './SortableLeadCard';

interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  value?: number;
  status: string;
  lead_source: string;
  notes?: string;
  last_contact_date?: string;
  next_follow_up?: string;
  created_at: string;
  updated_at: string;
  tags?: string[];
}

interface PipelineStage {
  id: string;
  name: string;
  order_index: number;
  color: string;
}

interface PipelineColumnProps {
  stage: PipelineStage;
  leads: Lead[];
  onLeadEdit?: (lead: Lead) => void;
  onAddNote?: (leadId: string) => void;
  onScheduleFollowUp?: (leadId: string) => void;
  onViewDetails?: (lead: Lead) => void;
}

const PipelineColumn: React.FC<PipelineColumnProps> = ({
  stage,
  leads,
  onLeadEdit,
  onAddNote,
  onScheduleFollowUp,
  onViewDetails
}) => {
  const { setNodeRef } = useDroppable({
    id: stage.id,
  });

  const stageValue = leads.reduce((sum, lead) => sum + (lead.value || 0), 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStageColor = (color: string) => {
    const colorMap: Record<string, string> = {
      '#64748b': 'border-slate-500',
      '#f59e0b': 'border-amber-500',
      '#ef4444': 'border-red-500',
      '#8b5cf6': 'border-violet-500',
      '#06b6d4': 'border-cyan-500',
      '#10b981': 'border-emerald-500',
      '#6b7280': 'border-gray-500',
    };
    return colorMap[color] || 'border-primary';
  };

  return (
    <Card className={`flex flex-col h-full border-t-4 ${getStageColor(stage.color)}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            {stage.name}
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {leads.length}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {formatCurrency(stageValue)}
        </p>
      </CardHeader>
      
      <CardContent className="flex-1 pt-0">
        <div
          ref={setNodeRef}
          className="space-y-3 min-h-[200px] pb-4"
        >
          <SortableContext 
            items={leads.map(lead => lead.id)} 
            strategy={verticalListSortingStrategy}
          >
            {leads.map((lead) => (
              <SortableLeadCard
                key={lead.id}
                lead={lead}
                onEdit={onLeadEdit}
                onAddNote={onAddNote}
                onScheduleFollowUp={onScheduleFollowUp}
                onViewDetails={onViewDetails}
              />
            ))}
          </SortableContext>
          
          {leads.length === 0 && (
            <div className="text-center text-muted-foreground text-sm py-8 border-2 border-dashed border-muted-foreground/20 rounded-lg">
              <p>Drop leads here</p>
              <p className="text-xs mt-1">or drag from other stages</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PipelineColumn;